import { maybe, rd, RemoteData } from '@passionware/monads';
import { Capability, FeatureFlag, IntegrationID } from '__types__';
import type { ResourceType } from '__types__/v1/models/ResourceType';
import useModal from 'hooks/useModal';
import AuthService from 'lib/auth/AuthService';
import Env from 'lib/env/Env';
import { useRestrictedAccessCallback } from 'lib/restrictedAccess/useRestrictedAccess';
import {
  BarChart3,
  BookOpenCheck,
  Calculator,
  Calendar,
  Camera,
  ChevronDown,
  FileText,
  GalleryHorizontal,
  Home,
  List,
  LogOut,
  Megaphone,
  Plus,
  PlusIcon,
  Search,
  Settings as SettingsIcon,
  Stamp,
  User,
  Users
} from 'lucide-react';
import { SplashlightDocuSignModal } from 'modals';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useRouteMatch } from 'react-router-dom';
import { selectActiveAccount } from 'store/accounts';
import { selectIntegrationById } from 'store/integrations';
import type { RootStore } from 'store/reducer';
import {
  selectBrandImage,
  getPublicCompanyName,
  selectAppId
} from 'store/v1/auth/auth.selectors';
import { getResourceTypes } from 'store/v1/contacts/contacts.selectors';
import { getProductionTypesList } from 'store/v1/production_types/production_types.selectors';
import { getBookingTypes } from 'store/v1/bookings/bookings.selectors';
import { openModal as dispatchOpenModal } from 'store/v1/ui/ui.actions';
import { ResourceTypeIcon } from 'v5/patterns/resource/ResourceTypeIcon';
import { withinApp, withinSettings } from 'v4/core/appRoutes';
import {
  useCurrentResourceId,
  useCurrentResourceTypeId
} from 'v4/entities/common/hooks/useCurrentResourceId';
import { KeyboardShortcut } from 'v5/patterns/widgets/KeyboardShortcut';
import { Button } from 'v5/design-system/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from 'v5/design-system/DropdownMenu';
import { Kbd } from 'v5/design-system/Kbd';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from 'v5/design-system/Tooltip';
import { myRouting } from 'v5/features/core/routing';
import { SandboxInfoPopover } from 'v5/features/core/sandbox-info/SandboxInfoPopover';
import { DockableSidebarToggle } from 'v5/patterns/sidebar/DockableLayout';
import {
  MenuCollapsible,
  MenuItem,
  MenuSeparator
} from 'v5/patterns/sidebar/Menu';
import {
  Sidebar,
  SidebarCollapse,
  SidebarContent,
  SidebarHeader
} from 'v5/patterns/sidebar/Sidebar';
import { SidebarHelpFooter } from 'v5/patterns/sidebar/SidebarHelpFooter';
import { cn } from 'v5/platform/dom/cn';
import { NavLink } from 'v5/platform/react-router';
import { WithServices } from 'v5/platform/ts/services';
import { WithDevOriginService } from 'v5/services/front/DevOriginService/DevOriginService';
import { WithFeatureService } from 'v5/services/front/FeatureService/FeatureService';
import { WithAccessService } from 'v5/services/front/AccessService/AccessService';
import { WithRoutingService } from 'v5/services/front/RoutingService/RoutingService';
import { WithOnlineStatusService } from 'v5/services/front/OnlineStatusService/OnlineStatusService';
import { WithSupportService } from 'v5/services/front/SupportService/SupportService';
import {
  WithContentThemeService,
  WithThemeService
} from 'v5/services/front/ThemeService/ThemeService';
import { WithUserPreferencesService } from 'v5/services/front/UserPreferencesService/UserPreferencesService';
import type { WithGlobalSearchService } from 'v5/services/GlobalSearchService/GlobalSearchService';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import { WithUserFlowService } from 'v5/services/front/UserFlowService/UserFlowService';
import { WithProductionService } from 'v5/services/io/ProductionService/ProductionService';
import { WithResourceService } from 'v5/services/io/ResourceService/ResourceService';
import { useShortlistCreateEntry } from 'v5/features/shortlists/create/useShortlistCreateEntry';
import { useResource } from '../productions/booking-costs/booking/useBookingEntity';
import { useCurrentTeamName } from 'v4/entities/common/hooks/useTeam';

export interface MainSidebarProps
  extends WithServices<
    [
      WithRoutingService,
      WithFeatureService,
      WithGlobalSearchService,
      WithSupportService,
      WithThemeService,
      WithContentThemeService,
      WithOnlineStatusService,
      WithDevOriginService,
      WithAppScopeService,
      WithAccessService,
      WithUserPreferencesService,
      WithUserFlowService,
      WithProductionService,
      WithResourceService
    ]
  > {
  className?: string;
}

/** One indent level under a section MenuCollapsible (Resources, Productions, etc.). */
const SUBMENU_LEVEL = 1;

// Local component for menu items with create action button
interface MenuItemWithCreateButtonProps {
  to: string;
  icon?: React.ReactNode;
  level?: number;
  onCreateClick?: (e: React.MouseEvent) => void;
  createTooltip: string;
  children: React.ReactNode;
  className?: string;
  isActiveOverride?: boolean;
  createButtonWrapper?: (node: React.ReactNode) => React.ReactNode;
  autoSelect?: boolean;
}

const MenuItemWithCreateButton: React.FC<MenuItemWithCreateButtonProps> = ({
  to,
  icon,
  level = 0,
  onCreateClick,
  createTooltip,
  children,
  className,
  isActiveOverride,
  createButtonWrapper,
  autoSelect
}) => {
  const createButton = (
    <div
      role="button"
      className={cn(
        'absolute right-1.5 top-1.5 size-7 flex items-center justify-center group-hover/menu-item:opacity-100 opacity-0 transition-all',
        'rounded bg-transparent! backdrop-blur-3xl hover:bg-bg-emphasis/5!'
      )}
      onClick={onCreateClick}
    >
      <PlusIcon className="size-4 shrink-0" />
    </div>
  );

  const createButtonWithTooltip = createButtonWrapper
    ? createButtonWrapper(createButton)
    : createButton;

  return (
    <NavLink to={to} isActiveOverride={isActiveOverride}>
      <MenuItem
        className={cn('group/menu-item relative', className)}
        level={level}
        icon={icon}
        autoSelect={autoSelect}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            {/* empty div solves problem with dynamic create button not always passing refs and props correctly to the trigger */}
            <div className="">{createButtonWithTooltip}</div>
          </TooltipTrigger>
          <TooltipContent side="right">{createTooltip}</TooltipContent>
        </Tooltip>
        {children}
      </MenuItem>
    </NavLink>
  );
};

interface ResourceTypeMenuItemProps
  extends WithServices<[WithResourceService]> {
  resourceType: ResourceType;
  href: string;
  onCreateClick: (resourceTypeId: number) => void;
  activeContactId: number | null;
}

interface ProductionTypeMenuItemProps
  extends WithServices<[WithProductionService]> {
  productionType: { id: number; name: string };
  href: string;
  onCreateClick: () => void;
}

const ResourceTypeMenuItem: React.FC<ResourceTypeMenuItemProps> = ({
  resourceType,
  href,
  onCreateClick,
  activeContactId,
  services
}) => {
  const contactResourceTypeId = useResource(
    services.resourceService,
    activeContactId
  )?.resource_type_id;
  const activeResourceTypeId = useCurrentResourceTypeId();

  const isActiveFromContact = maybe.mapOrElse(
    contactResourceTypeId,
    resourceTypeId => resourceTypeId === resourceType.id,
    false
  );
  const isActiveFromRoute = activeResourceTypeId === resourceType.id;

  const resourceName = resourceType.name ?? 'Resource';
  const createTooltip = `Create new ${resourceName.toLowerCase()}`;
  const handleCreateClick = React.useCallback(() => {
    onCreateClick(resourceType.id);
  }, [onCreateClick, resourceType.id]);

  return (
    <MenuItemWithCreateButton
      level={SUBMENU_LEVEL}
      to={href}
      icon={
        <ResourceTypeIcon
          icon={resourceType.icon}
          alt={resourceName}
          className="size-4 opacity-80"
        />
      }
      onCreateClick={handleCreateClick}
      createTooltip={createTooltip}
      isActiveOverride={
        isActiveFromContact || isActiveFromRoute ? true : undefined
      }
    >
      {resourceName}
    </MenuItemWithCreateButton>
  );
};

const ProductionTypeMenuItem: React.FC<ProductionTypeMenuItemProps> = ({
  services,
  productionType,
  href,
  onCreateClick
}) => {
  const appId = maybe.getOrUndefined(useCurrentTeamName());
  const productionTypeName = productionType.name || 'Production';
  const createTooltip = `Create new ${productionTypeName.toLowerCase()}`;

  // Check if we're on this production type's list page
  const isOnProductionTypePage = useRouteMatch({
    path: myRouting.forWorkspace(appId).productions().type(productionType.id),
    exact: true
  });

  const currentDetailRoute = useRouteMatch<{ id: string }>({
    path: myRouting.forWorkspace().productions().detail()
  });

  const currentProduction = services.productionService.useProduction(
    maybe.map(currentDetailRoute?.params?.id, Number),
    appId!
  );
  const isActiveFromProduction = rd.mapOrElse(
    currentProduction,
    production => {
      return production.production_type_id === productionType.id;
    },
    false
  );

  return (
    <MenuItemWithCreateButton
      level={SUBMENU_LEVEL}
      to={href}
      icon={<Camera className="size-4" />}
      onCreateClick={onCreateClick}
      createTooltip={createTooltip}
      isActiveOverride={!!isOnProductionTypePage || isActiveFromProduction}
    >
      {productionTypeName}
    </MenuItemWithCreateButton>
  );
};

interface BookingTypeMenuItemProps {
  bookingType: { id: number; name: string };
  href: string;
  onCreateClick: () => void;
}

const BookingTypeMenuItem: React.FC<BookingTypeMenuItemProps> = ({
  bookingType,
  href,
  onCreateClick
}) => {
  const appId = maybe.getOrUndefined(useCurrentTeamName());
  const bookingTypeName = bookingType.name || 'Booking';
  const createTooltip = `Create new ${bookingTypeName.toLowerCase()}`;

  // Check if we're on this booking type's list page
  const isOnBookingTypePage = useRouteMatch({
    path: myRouting.forWorkspace(appId).bookings().type(bookingType.id),
    exact: true
  });

  return (
    <MenuItemWithCreateButton
      level={SUBMENU_LEVEL}
      to={href}
      icon={<Stamp className="size-4" />}
      onCreateClick={onCreateClick}
      createTooltip={createTooltip}
      isActiveOverride={!!isOnBookingTypePage}
    >
      {bookingTypeName}
    </MenuItemWithCreateButton>
  );
};

export function MainSidebar({ services, className }: MainSidebarProps) {
  const can = useRestrictedAccessCallback(services);
  const appId = useSelector(selectAppId);
  const slugAppScope = services.appScopeService.useSlugAppScope();
  const companyName = useSelector(getPublicCompanyName);
  const brandImage = useSelector(selectBrandImage);
  const account = useSelector(selectActiveAccount);
  const resourceTypes = useSelector(getResourceTypes);
  const productionTypes = useSelector(getProductionTypesList);
  const bookingTypes = useSelector(getBookingTypes);
  const dispatch = useDispatch();
  const { openModal: openV2Modal } = useModal();
  const activeContactId = useCurrentResourceId();
  const activeResourceId = useCurrentResourceId();
  const isContactRoute = activeContactId != null;
  const isResourceRoute = activeResourceId != null;

  // Check if we're on the resources root page (all resources)
  const isOnResourcesRoot = useRouteMatch({
    path: myRouting.forWorkspace(appId).resources().root(),
    exact: true
  });

  // Check if we're on the productions root page (all productions)
  const isOnProductionsRoot = useRouteMatch({
    path: myRouting.forWorkspace(appId).productions().root(),
    exact: true
  });

  // Check if we're on the bookings root page (all bookings)
  const isOnBookingsRoot = useRouteMatch({
    path: myRouting.forWorkspace(appId).bookings().root(),
    exact: true
  });

  const to = withinApp(appId);
  const settingsTo = withinSettings();

  // Get keyboard shortcuts from preferences service
  const shortcuts = services.userPreferencesService.useShortcuts();

  const docusignIntegration = useSelector((state: RootStore) =>
    selectIntegrationById(state, IntegrationID.DOCUSIGN)
  );

  // Get company initial from company name
  const companyInitial = companyName?.charAt(0)?.toUpperCase() || 'A';

  // Check if we're in sandbox/QA environment
  const isSandbox = !Env.isProd || Env.isQAEnvironment;

  const handleLogout = () => {
    AuthService.logout();
  };

  const handleOpenSearch = () => {
    services.globalSearchService.openSearchEvent.emit();
  };

  const handleCreateResource = (resourceTypeId?: number) => {
    services.userFlowService.openCreateResourceModal({
      resourceTypeId,
      appScope: slugAppScope
    });
  };

  const handleCreateProduction = (productionTypeId?: number) => {
    dispatch(
      dispatchOpenModal(
        'ProductionSetUpModal',
        { create: true, productionTypeId },
        slugAppScope
      )
    );
  };

  const shortlistCreateEntry = useShortlistCreateEntry(services, null);
  const handleCreateShortlist = () => {
    if (shortlistCreateEntry === 'v5') {
      services.userFlowService.openCreateShortlistModal({
        appScope: slugAppScope
      });
      return;
    }
    dispatch(
      dispatchOpenModal('ShortlistCreateModal', { create: true }, slugAppScope)
    );
  };

  const handleCreateBooking = () => {
    dispatch(dispatchOpenModal('ResourceEventsModal', undefined, slugAppScope));
  };

  const handleCreateCallsheet = () => {
    services.userFlowService.openCreateCallsheetDialog(appId, {
      appScope: slugAppScope
    });
  };

  const handleCreateCallout = () => {
    void services.userFlowService.openCreateCalloutModal({
      appScope: slugAppScope
    });
  };

  const handleCreateDocuSignRequest = () => {
    openV2Modal(() => <SplashlightDocuSignModal />);
  };

  const canCreateAtLeastOne = can(null, null, [
    Capability.RESOURCE_CREATE,
    Capability.PRODUCTION_CREATE,
    Capability.SHORTLIST_CREATE,
    Capability.BOOKING_CREATE,
    Capability.CALLSHEET_CREATE,
    Capability.CALLOUT_CREATE,
    Capability.DOCUSIGN_SEND
  ]);

  const shouldShowCreateResourcesGroup =
    (resourceTypes?.length ?? 0) !== 0 &&
    can(FeatureFlag.RESOURCES, Capability.RESOURCE_CREATE);

  const shouldShowCreateEntitiesGroup =
    can(FeatureFlag.PRODUCTIONS, Capability.PRODUCTION_CREATE) ||
    can(FeatureFlag.SHORTLISTS, Capability.SHORTLIST_CREATE) ||
    can(FeatureFlag.BOOKINGS, Capability.BOOKING_CREATE) ||
    can(FeatureFlag.CALLSHEETS, Capability.CALLSHEET_CREATE) ||
    can(FeatureFlag.CALLOUTS, Capability.CALLOUT_CREATE);

  function renderShortcut(shortcut: RemoteData<string>) {
    return rd.tryMap(shortcut, shortcut => (
      <Kbd className="ml-auto">
        <KeyboardShortcut shortcut={shortcut} />
      </Kbd>
    ));
  }

  return (
    <Sidebar className={cn('group/sidebar', className)}>
      <SidebarHeader className="flex flex-col gap-2 min-h-[var(--header-height)]">
        <div className="flex items-center justify-between ml-3">
          <div className="flex items-center gap-2 flex-1">
            <div className="w-8 h-8 rounded bg-bg-subtle dark:border dark:border-white/20 flex items-center justify-center text-fg-muted text-base font-bold overflow-hidden relative">
              {brandImage ? (
                <img
                  src={brandImage}
                  alt={companyName}
                  className="absolute w-full h-full object-cover"
                />
              ) : (
                companyInitial
              )}
            </div>
            {isSandbox && <SandboxInfoPopover />}
          </div>
          <DockableSidebarToggle>
            <SidebarCollapse className="group-hocus/sidebar:opacity-100 opacity-0 transition-opacity" />
          </DockableSidebarToggle>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="w-auto self-start">
              <div className="text-xl">{companyName}</div>
              <ChevronDown className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" side="right" className="w-56">
            <DropdownMenuLabel>{companyName}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem asChild>
                <Link to={settingsTo.settings.home()}>
                  <SettingsIcon className="size-4" />
                  Settings
                </Link>
              </DropdownMenuItem>
            </DropdownMenuGroup>
            {account && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Your account</DropdownMenuLabel>
                <DropdownMenuGroup>
                  <DropdownMenuItem asChild>
                    <Link to={settingsTo.settings.account.details()}>
                      <User className="size-4" />
                      {account.displayName}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut className="size-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="flex flex-row gap-2">
          <Button
            variant="secondary"
            size="sm"
            className="flex-1 gap-3 justify-start relative pr-1.5"
            onClick={handleOpenSearch}
          >
            <Search className="size-4" />
            <span>Search</span>
            <Kbd className="ml-auto font-normal self-stretch rounded bg-bg-subtle-2">
              <KeyboardShortcut shortcut="mod+k" />
            </Kbd>
          </Button>

          {canCreateAtLeastOne && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" size="sm">
                  <Plus className="size-4" />
                  Create
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                side="right"
                className="max-w-[250px]"
              >
                {shouldShowCreateResourcesGroup && (
                  <>
                    <DropdownMenuLabel>New resource</DropdownMenuLabel>
                    <DropdownMenuGroup>
                      {resourceTypes.map(resourceType => (
                        <DropdownMenuItem
                          key={resourceType.id}
                          onClick={() => handleCreateResource(resourceType.id)}
                        >
                          <ResourceTypeIcon
                            icon={resourceType.icon}
                            alt={resourceType.name}
                            className="size-4 opacity-80"
                          />
                          <span className="truncate">{resourceType.name}</span>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuGroup>
                    {shouldShowCreateEntitiesGroup && <DropdownMenuSeparator />}
                  </>
                )}

                {shouldShowCreateEntitiesGroup && (
                  <>
                    <DropdownMenuLabel>New</DropdownMenuLabel>
                    <DropdownMenuGroup>
                      {can(
                        FeatureFlag.PRODUCTIONS,
                        Capability.PRODUCTION_CREATE
                      ) && (
                        <DropdownMenuItem
                          onClick={() => handleCreateProduction()}
                        >
                          <Camera className="size-4" />
                          Production
                          {renderShortcut(
                            rd.map(shortcuts, s => s.create.production)
                          )}
                        </DropdownMenuItem>
                      )}
                      {can(
                        FeatureFlag.SHORTLISTS,
                        Capability.SHORTLIST_CREATE
                      ) && (
                        <DropdownMenuItem onClick={handleCreateShortlist}>
                          <GalleryHorizontal className="size-4" />
                          Shortlist
                          {renderShortcut(
                            rd.map(shortcuts, s => s.create.shortlist)
                          )}
                        </DropdownMenuItem>
                      )}
                      {can(FeatureFlag.BOOKINGS, Capability.BOOKING_CREATE) && (
                        <DropdownMenuItem onClick={handleCreateBooking}>
                          <Stamp className="size-4" />
                          Booking
                          {renderShortcut(
                            rd.map(shortcuts, s => s.create.booking)
                          )}
                        </DropdownMenuItem>
                      )}
                      {can(
                        FeatureFlag.CALLSHEETS,
                        Capability.CALLSHEET_CREATE
                      ) && (
                        <DropdownMenuItem onClick={handleCreateCallsheet}>
                          <FileText className="size-4" />
                          Callsheet
                          {renderShortcut(
                            rd.map(shortcuts, s => s.create.callsheet)
                          )}
                        </DropdownMenuItem>
                      )}
                      {can(FeatureFlag.CALLOUTS, Capability.CALLOUT_CREATE) && (
                        <DropdownMenuItem onClick={handleCreateCallout}>
                          <Megaphone className="size-4" />
                          Callout
                          {renderShortcut(
                            rd.map(shortcuts, s => s.create.callout)
                          )}
                        </DropdownMenuItem>
                      )}
                      {can(
                        FeatureFlag.CUSTOM_SPLASHLIGHT,
                        Capability.DOCUSIGN_SEND,
                        null
                      ) &&
                        docusignIntegration?.enabled && (
                          <DropdownMenuItem
                            onClick={handleCreateDocuSignRequest}
                          >
                            <FileText className="size-4" />
                            Send DocuSign Request
                          </DropdownMenuItem>
                        )}
                    </DropdownMenuGroup>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <NavLink to={to.dashboard()}>
          <MenuItem icon={<Home className="size-4" />}>Dashboard</MenuItem>
        </NavLink>
      </SidebarHeader>

      <SidebarContent className="px-2 flex flex-col gap-2">
        {can(FeatureFlag.RESOURCES, Capability.RESOURCE_READ) && (
          <>
            <MenuCollapsible
              icon={<Users className="size-4" />}
              label="Resources"
              expandOnChildSelect
              defaultOpen={isContactRoute || isResourceRoute}
            >
              <MenuItemWithCreateButton
                level={SUBMENU_LEVEL}
                to={to.resources.home()}
                icon={<List className="size-4" />}
                // "All" names no type: the dialog asks for one when there are several.
                onCreateClick={() => handleCreateResource()}
                createTooltip="Create new resource"
                isActiveOverride={!!isOnResourcesRoot}
                autoSelect
              >
                All
              </MenuItemWithCreateButton>
              {resourceTypes?.map(resourceType => (
                <ResourceTypeMenuItem
                  services={services}
                  key={resourceType.id}
                  resourceType={resourceType}
                  href={to.resources.item(resourceType.id)}
                  onCreateClick={handleCreateResource}
                  activeContactId={activeContactId}
                />
              ))}
            </MenuCollapsible>

            <NavLink to={to.calendar.resources.home()}>
              <MenuItem icon={<Calendar className="size-4" />}>
                Resource calendar
              </MenuItem>
            </NavLink>
          </>
        )}

        {can(FeatureFlag.CALLOUTS, Capability.CALLOUT_READ) && (
          <MenuItemWithCreateButton
            to={to.callouts.home()}
            icon={<Megaphone className="size-4" />}
            onCreateClick={handleCreateCallout}
            createTooltip="Create new callout"
          >
            Callouts
          </MenuItemWithCreateButton>
        )}

        {can(FeatureFlag.SHORTLISTS, Capability.SHORTLIST_READ) && (
          <>
            <MenuSeparator />
            <MenuCollapsible
              icon={<GalleryHorizontal className="size-4" />}
              label="Shortlists"
              autoClickOnlyChild
            >
              {shortlistCreateEntry === 'none' ? (
                <NavLink to={to.shortlists.home()}>
                  <MenuItem
                    level={SUBMENU_LEVEL}
                    icon={<List className="size-4" />}
                  >
                    All
                  </MenuItem>
                </NavLink>
              ) : (
                <MenuItemWithCreateButton
                  level={SUBMENU_LEVEL}
                  to={to.shortlists.home()}
                  onCreateClick={handleCreateShortlist}
                  createTooltip="Create new shortlist"
                  icon={<List className="size-4" />}
                >
                  All
                </MenuItemWithCreateButton>
              )}
            </MenuCollapsible>
          </>
        )}

        {can(FeatureFlag.PRODUCTIONS, Capability.PRODUCTION_READ) && (
          <>
            <MenuSeparator />
            <MenuCollapsible
              icon={<Camera className="size-4" />}
              dataTestId="all-productions"
              label="Productions"
              expandOnChildSelect
            >
              <MenuItemWithCreateButton
                level={SUBMENU_LEVEL}
                to={to.productions.home()}
                icon={<List className="size-4" />}
                onCreateClick={() => handleCreateProduction()}
                createTooltip="Create new production"
                isActiveOverride={!!isOnProductionsRoot}
                autoSelect
              >
                All
              </MenuItemWithCreateButton>
              {productionTypes?.map(productionType => (
                <ProductionTypeMenuItem
                  key={productionType.id}
                  services={services}
                  productionType={productionType}
                  href={to.productions.type(productionType.id)}
                  onCreateClick={() =>
                    handleCreateProduction(productionType.id)
                  }
                />
              ))}
            </MenuCollapsible>

            <NavLink to={to.calendar.productions.home()}>
              <MenuItem icon={<Calendar className="size-4" />}>
                Production calendar
              </MenuItem>
            </NavLink>
          </>
        )}

        {can(FeatureFlag.CALLSHEETS, Capability.CALLSHEET_READ) && (
          <MenuItemWithCreateButton
            to={to.callsheets.home()}
            icon={<FileText className="size-4" />}
            onCreateClick={handleCreateCallsheet}
            createTooltip="Create new callsheet"
          >
            Callsheets
          </MenuItemWithCreateButton>
        )}

        {can(FeatureFlag.BOOKINGS, Capability.BOOKING_READ) && (
          <>
            <MenuSeparator />
            <MenuCollapsible
              icon={<Stamp className="size-4" />}
              label="Bookings"
              expandOnChildSelect
            >
              <MenuItemWithCreateButton
                level={SUBMENU_LEVEL}
                to={to.bookings.home()}
                icon={<List className="size-4" />}
                onCreateClick={handleCreateBooking}
                createTooltip="Create new booking"
                isActiveOverride={!!isOnBookingsRoot}
                autoSelect
              >
                All
              </MenuItemWithCreateButton>
              {bookingTypes?.map(bookingType => (
                <BookingTypeMenuItem
                  key={bookingType.id}
                  bookingType={bookingType}
                  href={to.bookings.type(bookingType.id)}
                  onCreateClick={handleCreateBooking}
                />
              ))}
            </MenuCollapsible>
          </>
        )}

        <MenuSeparator />

        {can(
          FeatureFlag.NIKECUSTOM_FRONT_DESK,
          Capability.CONFIGURE_CUSTOM_NIKE_FRONT_DESK
        ) && (
          <NavLink to={to.frontdesk.home()}>
            <MenuItem icon={<BookOpenCheck className="size-4" />}>
              Front desk
            </MenuItem>
          </NavLink>
        )}

        {can(FeatureFlag.RECONCILIATIONS, Capability.MANAGE_RECONCILIATION) && (
          <NavLink to={to.reconciliations.home()}>
            <MenuItem icon={<Calculator className="size-4" />}>
              Reconciliation
            </MenuItem>
          </NavLink>
        )}

        {can(FeatureFlag.REPORTING, Capability.REPORT_DOWNLOAD) && (
          <NavLink to={to.reporting.home()}>
            <MenuItem icon={<BarChart3 className="size-4" />}>
              Reporting
            </MenuItem>
          </NavLink>
        )}
      </SidebarContent>

      <SidebarHelpFooter services={services} />
    </Sidebar>
  );
}
